/**
 * Generate a UUID v4 for use as an idempotency key
 * @returns {string} UUID v4 string
 */
export const generateIdempotencyKey = () => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback to manual UUID v4 generation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : ((r & 0x3) | 0x8)
    return v.toString(16)
  })
}

/**
 * Store idempotency key in sessionStorage for a specific prospect
 * @param {string} prospectId - The prospect ID
 * @param {string} key - The idempotency key
 */
export const storeIdempotencyKey = (prospectId, key) => {
  try {
    sessionStorage.setItem(`idempotency_${prospectId}`, key)
  } catch (e) {
    // SessionStorage not available or quota exceeded
    console.warn('Could not store idempotency key:', e)
  }
}

/**
 * Get or create idempotency key for a specific prospect
 * @param {string} prospectId - The prospect ID
 * @returns {string} The idempotency key
 */
export const getOrCreateIdempotencyKey = (prospectId) => {
  try {
    const existing = sessionStorage.getItem(`idempotency_${prospectId}`)
    if (existing) {
      return existing
    }
  } catch (e) {
    // SessionStorage not available
    console.warn('Could not read idempotency key:', e)
  }

  const newKey = generateIdempotencyKey()
  storeIdempotencyKey(prospectId, newKey)
  return newKey
}

/**
 * Clear idempotency key for a specific prospect (e.g., after successful payment)
 * @param {string} prospectId - The prospect ID
 */
export const clearIdempotencyKey = (prospectId) => {
  try {
    sessionStorage.removeItem(`idempotency_${prospectId}`)
  } catch (e) {
    // SessionStorage not available
    console.warn('Could not clear idempotency key:', e)
  }
}
